/**
 * docs/UX.md 「示范录音」 — 杨爽老师 一次录完 T7 听辨题的全部参考音。
 *
 * 红线：示范音**必须是老师本人的声音**，平台没有、也不会有任何 TTS / 合成入口。
 *
 * 设计目标是「一口气录完 144 遍、不超过 15 分钟」：
 *   * 顶部一个录音台，永远告诉你「现在录哪一个」；
 *   * 保存成功自动跳到下一个没录的遍次，手不用离开录音键；
 *   * 下面按易混淆类分组的清单只是导航与复核，不打断录音流。
 */
import { useCallback, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AudioPlayer,
  Button,
  Card,
  Icon,
  LevelMeter,
  ProgressBar,
  RecordButton,
  Tag,
  useToast,
} from "@xinghai/ui";
import { ApiError, stimuliApi } from "../../lib/api";
import type { StimuliListResponse, StimulusSyllable } from "./types";
import { qk } from "../../lib/queries";
import { useRecorder } from "../../lib/audio/useRecorder";
import { PageHeader, QueryBoundary } from "./common";

/** One (音节 × 遍次) cell — the unit the teacher records. */
export interface Slot {
  syllable: string;
  take: number;
}

export const slotKey = (s: Slot): string => `${s.syllable}#${s.take}`;

/** 单个示范音的硬上限，与服务端 `MAX_STIMULUS_MS` 一致。 */
export const STIMULUS_MAX_MS = 5000;

/** Catalogue order, flattened: 类 → 音节 → 第 1 遍 → 第 2 遍. */
export function allSlots(data: StimuliListResponse | undefined): Slot[] {
  if (!data) return [];
  return data.groups.flatMap((g) =>
    g.syllables.flatMap((s) => s.takes.map((t) => ({ syllable: s.syllable, take: t.take }))),
  );
}

export function recordedSet(data: StimuliListResponse | undefined): Set<string> {
  const out = new Set<string>();
  for (const g of data?.groups ?? []) {
    for (const s of g.syllables) {
      for (const t of s.takes)
        if (t.recorded) out.add(slotKey({ syllable: s.syllable, take: t.take }));
    }
  }
  return out;
}

/**
 * The next take still missing, walking forward from `after` and wrapping once.
 * `null` when everything is recorded — that is the end of the pass.
 */
export function nextMissing(
  data: StimuliListResponse | undefined,
  recorded: ReadonlySet<string>,
  after: Slot | null,
): Slot | null {
  const slots = allSlots(data);
  if (slots.length === 0) return null;
  const from = after ? slots.findIndex((s) => slotKey(s) === slotKey(after)) : -1;
  for (let i = 1; i <= slots.length; i += 1) {
    const slot = slots[(from + i + slots.length) % slots.length]!;
    if (!recorded.has(slotKey(slot))) return slot;
  }
  return null;
}

export function findSyllable(
  data: StimuliListResponse | undefined,
  syllable: string,
): StimulusSyllable | null {
  for (const g of data?.groups ?? []) {
    const hit = g.syllables.find((s) => s.syllable === syllable);
    if (hit) return hit;
  }
  return null;
}

const TIPS = [
  "找个安静的房间，关掉风扇和空调外机。",
  "用平时说话的音量，正常语速读一遍就好。",
  "每个音单独读，不要连着上一条。",
  "两遍之间稍停一下，第二遍要是真正重录的一遍，不要复制第一遍。",
];

// --------------------------------------------------------------------------- //

export function StimuliPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({ queryKey: qk.stimuli, queryFn: () => stimuliApi.list() });
  const data = q.data;

  const recorded = useMemo(() => recordedSet(data), [data]);
  /** `undefined` = 还没选过，录音台自己停在第一个没录的遍次上；`null` = 全部录完。 */
  const [chosen, setChosen] = useState<Slot | null | undefined>(undefined);
  const recorder = useRecorder({ maxMs: STIMULUS_MAX_MS });
  const { reset: resetRecorder, cancel: cancelRecorder } = recorder;
  const fileInput = useRef<HTMLInputElement>(null);

  const active = chosen === undefined ? nextMissing(data, recorded, null) : chosen;
  const finished = active === null;

  const activeSyllable = active ? findSyllable(data, active.syllable) : null;
  const activeTake = activeSyllable?.takes.find((t) => t.take === active?.take) ?? null;

  const goTo = useCallback(
    (slot: Slot | null) => {
      cancelRecorder();
      resetRecorder();
      setChosen(slot);
    },
    [cancelRecorder, resetRecorder],
  );

  const upload = useMutation({
    mutationFn: async (vars: { slot: Slot; blob: Blob }) =>
      stimuliApi.upload(vars.slot.syllable, vars.slot.take, vars.blob, `${vars.slot.syllable}.wav`),
    onSuccess: async (_res, vars) => {
      const next = new Set(recorded);
      next.add(slotKey(vars.slot));
      await qc.invalidateQueries({ queryKey: qk.stimuli });
      const following = nextMissing(data, next, vars.slot);
      goTo(following);
      if (!following) toast.success("全部示范录音都录好了，辛苦了！");
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : "没能保存，请再试一次。");
    },
  });

  const remove = useMutation({
    mutationFn: (slot: Slot) => stimuliApi.remove(slot.syllable, slot.take),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.stimuli }),
    onError: () => toast.error("没能删除，请稍后再试。"),
  });

  const save = () => {
    if (!active || !recorder.result) return;
    upload.mutate({ slot: active, blob: recorder.result.blob });
  };

  const pickFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !active) return;
    upload.mutate({ slot: active, blob: file });
  };

  const toggleRecord = () => {
    if (recorder.status === "recording") void recorder.stop();
    else void recorder.start();
  };

  return (
    <div className="xz-adm xz-stim">
      <PageHeader
        title="示范录音"
        description="听辨题里学生听到的每一个音，都由老师本人在这里录一遍。系统不会合成任何声音。"
      />

      <QueryBoundary
        isPending={q.isPending}
        isError={q.isError}
        error={q.error}
        onRetry={() => void q.refetch()}
      >
        {data && (
          <>
            <Card padding="md" className="xz-stim__progress">
              <p className="gz-type-score">
                已录 {data.recorded_takes} / {data.total_takes}
              </p>
              <ProgressBar
                value={data.total_takes === 0 ? 0 : data.recorded_takes / data.total_takes}
                label="示范录音进度"
                tone={data.recorded_takes >= data.total_takes ? "success" : "info"}
              />
              <p className="gz-caption">
                {data.syllable_count} 个音，每个音录两遍（第二遍要真的重录一次）。
                现在可以出给学生的听辨题：
                {data.trials.ready} / {data.trials.total}
              </p>
            </Card>

            <Card title="录之前看一眼" padding="md" className="xz-stim__tips">
              <ul className="xz-stim__tiplist">
                {TIPS.map((t) => (
                  <li key={t}>
                    <Icon id="ui-check" size={16} /> {t}
                  </li>
                ))}
              </ul>
            </Card>

            {finished && (
              <p className="gz-notice" role="status">
                <Icon id="ui-check-circle" size={20} />
                这一轮全部录完了。想重录哪一个，点下面清单里的遍次就行。
              </p>
            )}

            {active && (
              <Card padding="lg" className="xz-stim__studio">
                <div className="xz-stim__now">
                  <span className="xz-stim__char">
                    {activeSyllable?.display ?? activeSyllable?.pinyin ?? active.syllable}
                  </span>
                  <span className="xz-stim__pinyin">
                    {activeSyllable?.pinyin ?? active.syllable}
                  </span>
                  <span className="gz-caption">
                    第 {active.take} 遍{activeSyllable?.note ? ` · ${activeSyllable.note}` : ""}
                    {activeSyllable ? ` · ${activeSyllable.confusion_label}` : ""}
                  </span>
                </div>

                <RecordButton
                  state={recorder.status === "recording" ? "recording" : "idle"}
                  onToggle={toggleRecord}
                  elapsedMs={recorder.elapsedMs}
                  maxMs={STIMULUS_MAX_MS}
                  label={recorder.status === "recording" ? "停止录音" : "开始录音"}
                />
                {recorder.status === "recording" && (
                  <LevelMeter
                    rms={recorder.level.rms}
                    peak={recorder.level.peak}
                    clipped={recorder.level.clipped}
                  />
                )}
                {recorder.errorMessage && (
                  <p className="gz-notice gz-notice--error" role="alert">
                    <Icon id="ui-error" size={20} />
                    {recorder.errorMessage}
                  </p>
                )}

                {recorder.previewUrl && (
                  <div className="xz-stim__review">
                    <AudioPlayer src={recorder.previewUrl} hideRate label="刚录的这一遍" />
                    <div className="xz-stim__actions">
                      <Button variant="secondary" onClick={() => resetRecorder()}>
                        <Icon id="ui-refresh" size={16} /> 重录
                      </Button>
                      <Button variant="primary" loading={upload.isPending} onClick={save}>
                        <Icon id="ui-check" size={16} /> 保存并录下一个
                      </Button>
                    </div>
                  </div>
                )}

                {activeTake?.recorded && activeTake.audio_url && (
                  <div className="xz-stim__existing">
                    <p className="gz-caption">这一遍已经录过，重录会覆盖它。</p>
                    <AudioPlayer
                      src={activeTake.audio_url}
                      hideRate
                      size="sm"
                      label="已录的这一遍"
                    />
                  </div>
                )}

                <div className="xz-stim__actions">
                  <input
                    ref={fileInput}
                    type="file"
                    accept="audio/wav,.wav"
                    className="xz-sr-only"
                    aria-label="上传这一遍的 WAV 文件"
                    onChange={pickFile}
                  />
                  <Button variant="ghost" onClick={() => fileInput.current?.click()}>
                    <Icon id="ui-upload" size={16} /> 改用文件上传
                  </Button>
                  <Button variant="ghost" onClick={() => goTo(nextMissing(data, recorded, active))}>
                    跳过这一个
                  </Button>
                </div>
              </Card>
            )}

            {data.groups.map((group) => (
              <Card
                key={group.confusion_class}
                title={group.label}
                subtitle={`${group.syllables.length} 个音`}
                padding="md"
              >
                <ul className="xz-stim__rows">
                  {group.syllables.map((s) => (
                    <li key={s.syllable} className="xz-stim__row">
                      <span className="xz-stim__rowname">
                        <span className="xz-stim__char">{s.display ?? s.pinyin}</span>
                        <span className="xz-stim__pinyin">{s.pinyin}</span>
                        {s.note && <span className="gz-caption">{s.note}</span>}
                        {s.used_by.length === 0 && (
                          <span className="gz-caption">备用（暂无试次用到）</span>
                        )}
                      </span>
                      <span className="xz-stim__takes">
                        {s.takes.map((t) => {
                          const slot = { syllable: s.syllable, take: t.take };
                          const isActive = active !== null && slotKey(active) === slotKey(slot);
                          return (
                            <button
                              key={t.take}
                              type="button"
                              className={`xz-stim__take${isActive ? " is-active" : ""}`}
                              data-recorded={t.recorded}
                              aria-pressed={isActive}
                              onClick={() => goTo(slot)}
                            >
                              <Icon
                                id={t.recorded ? "ui-check-circle" : "ui-microphone"}
                                size={16}
                              />
                              第 {t.take} 遍
                              <span className="gz-caption">{t.recorded ? "已录" : "未录"}</span>
                            </button>
                          );
                        })}
                        {s.takes.some((t) => t.recorded) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            loading={remove.isPending}
                            onClick={() => {
                              const done = s.takes.filter((t) => t.recorded);
                              const last = done[done.length - 1]!;
                              remove.mutate({ syllable: s.syllable, take: last.take });
                            }}
                          >
                            删除最后一遍
                          </Button>
                        )}
                      </span>
                      {s.used_by.length > 0 && (
                        <span className="xz-adm__tags">
                          {[...new Set(s.used_by.map((u) => u.trial_type))].map((t) => (
                            <Tag key={t} tone="neutral">
                              {t}
                            </Tag>
                          ))}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </Card>
            ))}
          </>
        )}
      </QueryBoundary>
    </div>
  );
}
